/**
 * Homingo+ - the paid membership.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE CHARGES ANYBODY.
 * ---------------------------------------------------------------------------
 * A subscription needs a recurring-payment mandate (UPI autopay or a card on
 * file), a renewal job, a dunning path for failed charges and a way to cancel
 * that actually stops the next debit. None of that exists. `plans` below is a
 * price list, and the screen that renders it routes its button to the same
 * "not available yet" gate every other unbuilt control uses.
 *
 * Taking a payment for a benefit that cannot be delivered is not a stub, it
 * is a charge - which is why nothing here opens a checkout.
 *
 * The PERKS are the part worth getting right now, because they are what a
 * real implementation has to enforce: each one maps to a rule somewhere else
 * in the app - a discount at checkout, a waived visit fee, a priority flag on
 * dispatch. They are written as promises the platform can keep.
 */

import { IconName } from '../components/ui/Icon';

export type PlanKey = 'monthly' | 'yearly';

export type Plan = {
  key: PlanKey;
  /** The tab's label - "Monthly". */
  label: string;
  /** The card's heading - "Monthly Plan". */
  title: string;
  /** What it costs, in whole rupees, for the whole term. */
  price: number;
  /**
   * The list price this term is discounted from - the struck-through figure.
   *
   * Only on the yearly plan; `undefined` on monthly, which has nothing to be
   * struck through against. See `YEARLY_LIST_PRICE` for where the number
   * comes from and what it does and does not claim.
   */
  was?: number;
  /** How the charge recurs, spelled out - "billed monthly". */
  billed: string;
  /** Percentage saved against paying monthly. Zero on the monthly plan. */
  saving: number;
  /** Rupees saved over a year. Zero on the monthly plan. */
  savingRupees: number;
  /** Marks the plan the design highlights. Exactly one is true. */
  recommended: boolean;
  /** The ticks inside the card. */
  points: string[];
};

const MONTHLY_PRICE = 299;
const YEARLY_PRICE = 2999;

/**
 * The yearly plan's list price - what the struck-through figure shows.
 *
 * Set explicitly rather than derived as twelve months at the monthly rate,
 * which would be 3,588 and a 16% saving. This is a real list price the annual
 * term is discounted from, and it is the one number on this page that a
 * customer could accuse us of inventing - so it is stated once, here, and
 * every figure the page shows is computed from it.
 *
 * If the commercial decision changes to "twelve months at the monthly rate",
 * make this `MONTHLY_PRICE * 12` and the percentage and the rupees follow on
 * their own.
 */
const YEARLY_LIST_PRICE = 3749;

/**
 * The two terms.
 *
 * Every derived figure - the struck-through price, the percentage, the rupees
 * saved - is computed from the two prices above rather than typed out
 * separately. A hand-written "Save 20%" beside a pair of prices that do not
 * actually differ by 20% is the kind of thing a customer checks with a
 * calculator exactly once.
 */
export const plans: Plan[] = [
  {
    key: 'monthly',
    label: 'Monthly',
    title: 'Monthly Plan',
    price: MONTHLY_PRICE,
    billed: 'billed monthly',
    saving: 0,
    savingRupees: 0,
    recommended: false,
    points: ['All member perks', 'Cancel anytime', 'Billed every month'],
  },
  {
    key: 'yearly',
    label: 'Yearly',
    title: 'Yearly Plan',
    price: YEARLY_PRICE,
    was: YEARLY_LIST_PRICE,
    billed: 'billed annually',
    saving: Math.round((1 - YEARLY_PRICE / YEARLY_LIST_PRICE) * 100),
    savingRupees: YEARLY_LIST_PRICE - YEARLY_PRICE,
    recommended: true,
    points: [
      'All member perks',
      'Cancel anytime',
      `Save ₹${YEARLY_LIST_PRICE - YEARLY_PRICE} every year`,
    ],
  },
];

/** The plan for a key. Falls back to the first, so this cannot return null. */
export const planFor = (key: PlanKey): Plan =>
  plans.find(item => item.key === key) ?? plans[0];

// --------------------------------------------------------------- the pitch

export type Highlight = {
  icon: IconName;
  /** Two lines, because that is how the strip is drawn - a title and a tail. */
  title: string;
  detail: string;
};

/**
 * The four-up strip under the banner: why this exists, in four words each.
 *
 * Deliberately vaguer than `perks` below, and that is the division of labour -
 * this strip is scanned in a second on the way past, the perks list is read.
 * Saying the same thing twice at two levels of detail is what lets someone
 * decide whether to keep reading.
 */
export const highlights: Highlight[] = [
  { icon: 'tag', title: 'Save more', detail: 'every year' },
  { icon: 'bolt', title: 'Priority', detail: 'in all bookings' },
  { icon: 'refresh', title: 'Flexible &', detail: 'hassle-free' },
  { icon: 'heart', title: 'Premium care,', detail: 'anytime' },
];

export type Perk = {
  icon: IconName;
  title: string;
  /** What it actually means, in one line. No adjectives without a number. */
  detail: string;
};

/**
 * What membership buys.
 *
 * Every line is a number or a rule, never a mood. "Priority booking" on its
 * own means nothing to someone deciding whether to pay ₹299; "your request
 * reaches professionals before others" says who is affected and how.
 */
export const perks: Perk[] = [
  {
    icon: 'tag',
    title: 'Extra discounts',
    detail: 'Get up to 20% off on every booking',
  },
  {
    icon: 'bolt',
    title: 'Free priority slots',
    detail: 'Skip the wait and get priority booking',
  },
  {
    icon: 'refresh',
    title: 'Easier rescheduling',
    detail: 'Reschedule or modify bookings with ease',
  },
  {
    icon: 'headset',
    title: 'Premium chat support',
    detail: 'Faster responses from our support team',
  },
  {
    icon: 'gift',
    title: 'Referral bonus boost',
    detail: 'Earn extra when your friends join Homingo+',
  },
];

// ------------------------------------------------------------------ the FAQ

export type Faq = {
  question: string;
  answer: string;
};

/**
 * The questions somebody actually asks before paying.
 *
 * Answered plainly, including the awkward one about what happens when the
 * free cancellations run out - a FAQ that only answers the easy questions is
 * a FAQ nobody trusts.
 */
export const faqs: Faq[] = [
  {
    question: 'Can I cancel my Homingo+ membership anytime?',
    answer:
      'Yes. Cancelling stops the next renewal - it does not end the term you have already paid for, so your benefits run to the end of it.',
  },
  {
    question: 'What happens after my free cancellations are used?',
    answer:
      'Normal cancellation rules apply: free up to 6 hours before the visit, and a ₹49 fee inside that window.',
  },
  {
    question: 'Do the discounts apply to parts and materials?',
    answer:
      'No. Member discounts apply to the service charge. Parts, materials and anything the professional buys for the job are billed at cost.',
  },
  {
    question: 'Can I share my membership with family?',
    answer:
      'Membership is tied to one account and phone number. Anyone booking from that account gets the benefits, whatever address they book to.',
  },
];

// ------------------------------------------------------------ the account

/** The membership state for an account. Only ever inactive for now. */
export type Membership = {
  active: boolean;
  /** The plan on file, when active. */
  plan?: PlanKey;
  /** When it next renews, already formatted. */
  renewsOn?: string;
};

/**
 * Whether this account has Homingo+.
 *
 * Hardcoded to inactive, and it must stay that way until there is a server
 * field to read: showing someone an active membership they never bought means
 * showing them benefits that will not be applied at checkout.
 */
export const membershipFor = (): Membership => ({ active: false });
